import { useAuthorizationContext } from "../../../context";
import { useSnackbar } from "@powerdiary/components-library/lib";
import { AxiosError } from "axios";
import {
    useClientPortalSelector,
    useClientPortalDispatch,
    selectBooking,
    completeBookingAndClear,
    clearBookingState,
    setAvailability,
} from "../../../redux";
import { IPaymentData } from "../bookingSlice.types";
import { DateTime } from "luxon";
import { AppointmentBookingFailedApiDetailsErrorResponse, AppointmentBookingRequest } from "types/generated/models";
import { usePostMutation } from "hooks/useClientPortalQuery";
import { useEffect } from "react";
import { useAnalytics } from "hooks/useAnalytics";
import { BookingType } from "types/appointmentBooking";
import { useAppointmentBookingApi } from "../../../hooks/useAppointmentBookingApi";

export interface PaymentInfo {
    paymentIntentId: string;
    hasTotalAmount: boolean;
    idempotencyKey: string;
}

export type ConfirmBookingFunction = (clientsLog: string, paymentInfo?: PaymentInfo) => Promise<void>;

export interface IBookingConfirmationHook {
    confirmBooking: ConfirmBookingFunction;
    isLoading: boolean;
    isSuccess: boolean;
}

export function useBookingConfirmation(): IBookingConfirmationHook {
    const { getEndpoint } = useAppointmentBookingApi();
    const { userDetails } = useAuthorizationContext();
    const { enqueueSnackbar } = useSnackbar();
    const { location, service, provider, availability, confirmation, session } = useClientPortalSelector(selectBooking);
    const dispatch = useClientPortalDispatch();
    const { track } = useAnalytics();

    const { mutateAsync, isPending, isSuccess, error } = usePostMutation<
        { appointmentId?: number },
        AxiosError<AppointmentBookingFailedApiDetailsErrorResponse>,
        AppointmentBookingRequest
    >({
        url: getEndpoint("Schedule"),
    });

    useEffect(() => {
        if (error) {
            if (error?.response?.data?.details?.resetBooking) {
                dispatch(clearBookingState());
            }
        }
    }, [error, dispatch]);

    const confirmBooking = async (clientLogs: string, paymentInfo: PaymentInfo) => {
        const locationId = location.data?.locationId;
        const timeEntry = availability.data?.timeEntry;
        const notes = confirmation.data?.notes;
        const clientId = confirmation.data?.client?.id ?? userDetails?.clientId;
        const bookingType = session.bookingType;

        if (!locationId || !timeEntry?.dateTimeOffset) {
            throw new Error("Missing required booking data");
        }

        if (bookingType === BookingType.CLIENT) {
            const serviceId = service.data?.serviceId;
            const providerId = provider.data?.providerId;

            if (!serviceId || !providerId) {
                throw new Error("Missing required service or provider data for client booking");
            }
        }

        const dateTime = DateTime.fromISO(timeEntry.dateTimeOffset);
        const dateForAPI = dateTime.toISO({ includeOffset: false });

        const bookingRequest: AppointmentBookingRequest = {
            clientId: clientId,
            locationId,
            serviceId: service.data?.serviceId,
            providerId: provider.data?.providerId,
            appointmentId: timeEntry.appointmentId,
            date: dateForAPI,
            offset: dateTime.offset,
            durationMinutes: timeEntry.duration,
            clientLogs: clientLogs,
            type: bookingType,
            notes: notes || undefined,
            ...(paymentInfo
                ? {
                      paymentIntentId: paymentInfo.paymentIntentId,
                      hasTotalAmount: paymentInfo.hasTotalAmount,
                      idempotencyKey: paymentInfo.idempotencyKey,
                  }
                : {}),
        };

        const scheduleResponse = await mutateAsync(bookingRequest);

        if (scheduleResponse?.appointmentId != null && availability.data) {
            dispatch(
                setAvailability({
                    ...availability.data,
                    timeEntry: {
                        ...availability.data.timeEntry,
                        appointmentId: scheduleResponse.appointmentId,
                    },
                })
            );
        }

        const paymentData = paymentInfo
            ? ({
                  hasPaid: true,
                  hasTotalAmount: paymentInfo.hasTotalAmount,
              } as IPaymentData)
            : null;

        dispatch(completeBookingAndClear(paymentData));

        track("booking_completed", {
            booking_type: bookingType,
            location_id: locationId,
            service_id: service.data?.serviceId,
            service_name: service.data?.serviceName,
            provider_id: provider.data?.providerId,
            provider_name: provider.data?.providerName,
            appointment_date: dateForAPI,
            duration_minutes: timeEntry.duration,
            has_notes: !!notes,
        });

        const successMessage =
            bookingType === BookingType.CLIENT
                ? "Appointment booked successfully!"
                : "Class booking confirmed successfully!";

        enqueueSnackbar(successMessage, {
            variant: "success",
        });
    };

    return {
        confirmBooking,
        isLoading: isPending,
        isSuccess,
    };
}
